import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaTrophy } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './OverallPerformance.css';

// Slot order/hexes are the validated default categorical palette (dataviz skill,
// references/palette.md) - passes the adjacent-pair CVD + normal-vision checks in both
// themes against this app's actual surfaces. Wired as CSS vars in theme.css so they swap
// with light/dark automatically, same mechanism as every other themed color in the app.
const PIE_COLORS = [
  'var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)',
  'var(--chart-4)', 'var(--chart-5)', 'var(--chart-other)',
];

// Charts cap at the top N by value so bars/slices stay legible - the full data is
// always still in the table alongside each chart (this is also the "relief" the
// palette's contrast WARN requires for its lighter slots).
const CHART_TOP_N = 10;
const BRANCH_PIE_TOP_N = 5;

// Compact axis tick labels (1.2K / 3.4M) so large KES amounts don't crowd the axis.
const formatCompact = (n) => {
  const num = Number(n) || 0;
  if (Math.abs(num) >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (Math.abs(num) >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
  return `${num}`;
};

const chartTooltipStyle = {
  fontSize: '0.8rem',
  borderRadius: 6,
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  color: 'var(--text-primary)',
};

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatter = (amount) =>
  Number(amount || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function OverallPerformance() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentCode] = useState('');
  const [agentName, setAgentName] = useState('');
  const [isSuperAgent, setIsSuperAgent] = useState(null); // null = still checking

  // ── Date filter ──
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // ── Agents by production ──
  const [performance, setPerformance] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);

  // ── Performance by branch ──
  const [branchPerformance, setBranchPerformance] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);

  // ── Product consumption ──
  const [productRows, setProductRows] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productSortBy, setProductSortBy] = useState('product'); // 'product' | 'agent'

  // ══════════════════════════════════════
  //  API HELPER
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => ({
    'Authorization': `Basic ${credentials}`,
    'Content-Type': 'application/json',
  }), [credentials]);

  const apiFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, {
      method: options.method || 'GET',
      headers: authHeaders(),
      ...options,
    });
    return res.json();
  }, [authHeaders]);

  // ══════════════════════════════════════
  //  INIT — credentials, agent
  // ══════════════════════════════════════
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsAuth(true);
      } catch (err) { toast.error('Failed to load credentials'); }
    })();
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const dealer = await apiFetch(`${API}/getDealerdetails?email=${agent}`);
        setAgentCode(dealer.OID);
        setAgentName(dealer.Dealer_name);
        setIsSuperAgent(dealer.IsSuperAgent === 1);
      } catch (err) {
        toast.error('Failed to load agent details');
        setIsSuperAgent(false);
      }
    })();
  }, [agent, isAuth, apiFetch]);

  // ── Restrict this page to super agents only ──
  useEffect(() => {
    if (isSuperAgent === false) {
      toast.error('You do not have access to Overall Performance');
      navigate('/dashboard');
    }
  }, [isSuperAgent, navigate]);

  // ══════════════════════════════════════
  //  DATA FETCHING
  // ══════════════════════════════════════
  const buildDateParams = useCallback(() => {
    let params = '';
    if (startDate) params += `&start=${new Date(startDate).getTime()}`;
    if (endDate) params += `&end=${new Date(endDate).getTime()}`;
    return params;
  }, [startDate, endDate]);

  const fetchPerformance = useCallback(async () => {
    if (!agentCode || !isAuth) return;
    try {
      setPerformanceLoading(true);
      const data = await apiFetch(`${API}/getAgentsPerformance?requestingAgent=${agentCode}${buildDateParams()}`);
      setPerformance(Array.isArray(data) ? data : []);
    } catch (err) { toast.error('Failed to load agent performance'); }
    finally { setPerformanceLoading(false); }
  }, [agentCode, isAuth, apiFetch, buildDateParams]);

  const fetchBranchPerformance = useCallback(async () => {
    if (!agentCode || !isAuth) return;
    try {
      setBranchLoading(true);
      const data = await apiFetch(`${API}/getBranchPerformance?requestingAgent=${agentCode}${buildDateParams()}`);
      setBranchPerformance(Array.isArray(data) ? data : []);
    } catch (err) { toast.error('Failed to load branch performance'); }
    finally { setBranchLoading(false); }
  }, [agentCode, isAuth, apiFetch, buildDateParams]);

  const fetchProductConsumption = useCallback(async () => {
    if (!agentCode || !isAuth) return;
    try {
      setProductLoading(true);
      const data = await apiFetch(`${API}/getProductConsumption?requestingAgent=${agentCode}${buildDateParams()}`);
      setProductRows(Array.isArray(data) ? data : []);
    } catch (err) { toast.error('Failed to load product consumption'); }
    finally { setProductLoading(false); }
  }, [agentCode, isAuth, apiFetch, buildDateParams]);

  useEffect(() => { fetchPerformance(); }, [fetchPerformance]);
  useEffect(() => { fetchBranchPerformance(); }, [fetchBranchPerformance]);
  useEffect(() => { fetchProductConsumption(); }, [fetchProductConsumption]);

  const applyFilters = () => {
    fetchPerformance();
    fetchBranchPerformance();
    fetchProductConsumption();
  };

  // ══════════════════════════════════════
  //  DERIVED STATE
  // ══════════════════════════════════════
  const performanceTotal = useMemo(
    () => performance.reduce((sum, r) => sum + Number(r.TotalGrossPremium || 0), 0),
    [performance]
  );

  const branchPerformanceTotal = useMemo(
    () => branchPerformance.reduce((sum, r) => sum + Number(r.TotalGrossPremium || 0), 0),
    [branchPerformance]
  );

  // Aggregated per-product totals across all agents (overview above the detail table)
  const productTotals = useMemo(() => {
    const map = new Map();
    for (const r of productRows) {
      const key = r.ProductCode;
      const existing = map.get(key) || { ProductCode: r.ProductCode, ProductDescription: r.ProductDescription, PolicyCount: 0, TotalGrossPremium: 0 };
      existing.PolicyCount += Number(r.PolicyCount || 0);
      existing.TotalGrossPremium += Number(r.TotalGrossPremium || 0);
      map.set(key, existing);
    }
    return [...map.values()].sort((a, b) => b.TotalGrossPremium - a.TotalGrossPremium);
  }, [productRows]);

  const sortedProductRows = useMemo(() => {
    const rows = [...productRows];
    if (productSortBy === 'agent') {
      rows.sort((a, b) => a.Dealer_name.localeCompare(b.Dealer_name) || b.TotalGrossPremium - a.TotalGrossPremium);
    } else {
      rows.sort((a, b) => a.ProductDescription.localeCompare(b.ProductDescription) || b.TotalGrossPremium - a.TotalGrossPremium);
    }
    return rows;
  }, [productRows, productSortBy]);

  // ══════════════════════════════════════
  //  CHART DATA
  // ══════════════════════════════════════
  const agentsChartData = useMemo(
    () => performance
      .filter((r) => Number(r.TotalGrossPremium) > 0)
      .slice(0, CHART_TOP_N)
      .map((r) => ({ name: r.Dealer_name, value: Number(r.TotalGrossPremium || 0) })),
    [performance]
  );

  // Top N branches as their own slice, the rest folded into a single "Other" slice -
  // a pie with one slice per branch (there can be 20+) would be unreadable and would
  // exceed the categorical palette's safe slot count.
  const branchPieData = useMemo(() => {
    const withProduction = [...branchPerformance]
      .filter((r) => Number(r.TotalGrossPremium) > 0)
      .sort((a, b) => Number(b.TotalGrossPremium) - Number(a.TotalGrossPremium));
    const top = withProduction.slice(0, BRANCH_PIE_TOP_N);
    const rest = withProduction.slice(BRANCH_PIE_TOP_N);
    const otherTotal = rest.reduce((sum, r) => sum + Number(r.TotalGrossPremium || 0), 0);
    const data = top.map((r) => ({ name: r.BranchName, value: Number(r.TotalGrossPremium || 0) }));
    if (otherTotal > 0) data.push({ name: 'Other', value: otherTotal });
    return data;
  }, [branchPerformance]);

  const productChartData = useMemo(
    () => productTotals
      .filter((p) => Number(p.TotalGrossPremium) > 0)
      .slice(0, CHART_TOP_N)
      .map((p) => ({ name: p.ProductDescription, value: Number(p.TotalGrossPremium || 0) })),
    [productTotals]
  );

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  if (!isSuperAgent) return null; // still checking access, or redirect in flight

  return (
    <div className="op-page">
      <Header userName={agentName || agent} />
      <Sidebar />
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="op-main">
        {/* ── Top row ── */}
        <div className="op-top-row">
          <span className="op-company">DEFINITE ASSURANCE</span>
          <span className="op-breadcrumb">My Agents / Overall Performance</span>
        </div>

        <button className="op-back-btn" onClick={() => navigate('/my-agents')}>
          <FaArrowLeft style={{ marginRight: 6 }} /> Back to My Agents
        </button>

        {/* ── Date filter ── */}
        <div className="op-card">
          <div className="op-filter-bar">
            <div className="op-filter-group">
              <label>From</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="op-filter-group">
              <label>To</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <button className="op-apply-btn" onClick={applyFilters}>Apply Filters</button>
          </div>
        </div>

        {/* ══════════════════════════════
            AGENTS BY PRODUCTION
           ══════════════════════════════ */}
        <div className="op-card">
          <h3><FaTrophy style={{ marginRight: 8 }} />Agents by Production</h3>

          {performanceLoading ? (
            <div className="op-loading"><div className="op-spinner" /><p>Loading agent performance...</p></div>
          ) : performance.length === 0 ? (
            <div className="op-empty">No agents found.</div>
          ) : (
            <div className="op-split">
              <div className="op-table-col">
                <div className="op-table-wrap">
                  <table className="op-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Agent</th>
                        <th>Agent Code</th>
                        <th>Policies</th>
                        <th>Gross Premium (KES)</th>
                        <th>Net Commission (KES)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performance.map((r, i) => (
                        <tr key={r.OID}>
                          <td>{i + 1}</td>
                          <td>{r.Dealer_name}</td>
                          <td>{r.Dealer_code}</td>
                          <td>{r.PolicyCount}</td>
                          <td>{formatter(r.TotalGrossPremium)}</td>
                          <td>{formatter(r.TotalNetCommission)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="op-total">Total Gross Premium: <strong>KES {formatter(performanceTotal)}</strong></p>
              </div>

              <div className="op-chart-col">
                <p className="op-chart-title">
                  Gross Premium by Agent{agentsChartData.length < CHART_TOP_N ? '' : ` (Top ${CHART_TOP_N})`}
                </p>
                {agentsChartData.length === 0 ? (
                  <div className="op-chart-empty">No production to chart yet.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(220, agentsChartData.length * 36)}>
                    <BarChart data={agentsChartData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" horizontal={false} />
                      <XAxis type="number" tickFormatter={formatCompact} tick={{ fontSize: 11, fill: 'var(--text-faint)' }} />
                      <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                      <Tooltip formatter={(value) => [`KES ${formatter(value)}`, 'Gross Premium']} contentStyle={chartTooltipStyle} />
                      <Bar dataKey="value" name="Gross Premium" fill="var(--accent)" radius={[0, 4, 4, 0]} maxBarSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ══════════════════════════════
            PERFORMANCE BY BRANCH
           ══════════════════════════════ */}
        <div className="op-card">
          <h3><FaTrophy style={{ marginRight: 8 }} />Performance by Branch</h3>

          {branchLoading ? (
            <div className="op-loading"><div className="op-spinner" /><p>Loading branch performance...</p></div>
          ) : branchPerformance.length === 0 ? (
            <div className="op-empty">No branches found.</div>
          ) : (
            <div className="op-split">
              <div className="op-table-col">
                <div className="op-table-wrap">
                  <table className="op-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Branch</th>
                        <th>Agents</th>
                        <th>Policies</th>
                        <th>Gross Premium (KES)</th>
                        <th>Net Commission (KES)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branchPerformance.map((r, i) => (
                        <tr key={r.BranchOID}>
                          <td>{i + 1}</td>
                          <td>{r.BranchName}</td>
                          <td>{r.AgentCount}</td>
                          <td>{r.PolicyCount}</td>
                          <td>{formatter(r.TotalGrossPremium)}</td>
                          <td>{formatter(r.TotalNetCommission)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="op-total">Total Gross Premium: <strong>KES {formatter(branchPerformanceTotal)}</strong></p>
              </div>

              <div className="op-chart-col">
                <p className="op-chart-title">
                  Gross Premium Share by Branch{branchPieData.some((d) => d.name === 'Other') ? ` (Top ${BRANCH_PIE_TOP_N})` : ''}
                </p>
                {branchPieData.length === 0 ? (
                  <div className="op-chart-empty">No production to chart yet.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={branchPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} paddingAngle={2}>
                        {branchPieData.map((entry, i) => (
                          <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="var(--surface)" strokeWidth={2} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`KES ${formatter(value)}`, 'Gross Premium']} contentStyle={chartTooltipStyle} />
                      <Legend
                        wrapperStyle={{ fontSize: '0.76rem' }}
                        formatter={(value) => <span style={{ color: 'var(--text-secondary)' }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ══════════════════════════════
            PRODUCT CONSUMPTION
           ══════════════════════════════ */}
        <div className="op-card">
          <h3>Product Consumption</h3>

          {productLoading ? (
            <div className="op-loading"><div className="op-spinner" /><p>Loading product consumption...</p></div>
          ) : productRows.length === 0 ? (
            <div className="op-empty">No production found for the selected filters.</div>
          ) : (
            <>
              <h4 className="op-subheading">Totals by Product</h4>
              <div className="op-split">
                <div className="op-table-col">
                  <div className="op-table-wrap">
                    <table className="op-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Policies</th>
                          <th>Gross Premium (KES)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productTotals.map((p) => (
                          <tr key={p.ProductCode}>
                            <td>{p.ProductDescription}</td>
                            <td>{p.PolicyCount}</td>
                            <td>{formatter(p.TotalGrossPremium)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="op-chart-col">
                  <p className="op-chart-title">
                    Gross Premium by Product{productChartData.length < CHART_TOP_N ? '' : ` (Top ${CHART_TOP_N})`}
                  </p>
                  {productChartData.length === 0 ? (
                    <div className="op-chart-empty">No production to chart yet.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(220, productChartData.length * 36)}>
                      <BarChart data={productChartData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" horizontal={false} />
                        <XAxis type="number" tickFormatter={formatCompact} tick={{ fontSize: 11, fill: 'var(--text-faint)' }} />
                        <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                        <Tooltip formatter={(value) => [`KES ${formatter(value)}`, 'Gross Premium']} contentStyle={chartTooltipStyle} />
                        <Bar dataKey="value" name="Gross Premium" fill="var(--accent)" radius={[0, 4, 4, 0]} maxBarSize={22} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="op-subheading-row">
                <h4 className="op-subheading">Breakdown by Agent</h4>
                <div className="op-sort-toggle">
                  <button className={productSortBy === 'product' ? 'active' : ''} onClick={() => setProductSortBy('product')}>Group by Product</button>
                  <button className={productSortBy === 'agent' ? 'active' : ''} onClick={() => setProductSortBy('agent')}>Group by Agent</button>
                </div>
              </div>
              <div className="op-table-wrap">
                <table className="op-table">
                  <thead>
                    <tr>
                      <th>{productSortBy === 'agent' ? 'Agent' : 'Product'}</th>
                      <th>{productSortBy === 'agent' ? 'Product' : 'Agent'}</th>
                      <th>Policies</th>
                      <th>Gross Premium (KES)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProductRows.map((r, i) => (
                      <tr key={`${r.AgentOID}-${r.ProductCode}-${i}`}>
                        <td>{productSortBy === 'agent' ? r.Dealer_name : r.ProductDescription}</td>
                        <td>{productSortBy === 'agent' ? r.ProductDescription : r.Dealer_name}</td>
                        <td>{r.PolicyCount}</td>
                        <td>{formatter(r.TotalGrossPremium)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
